import { matchMarkdownBlocks } from "./markdownBlockMatcher";
import { parseMarkdownBlocks } from "./markdownBlockParser";
import { calculateTableDiffMarkdown } from "./markdownContentHandler";
import { MatchedBlockPair } from "./markdownDiffTypes";
import { renderInlineValueDiff } from "./markdownInlineDiff";
import { renderTextBlockDiff } from "./markdownTextDiff";

export const calculateMarkdownDiff = ({
  previousContent,
  currentContent,
}: {
  previousContent: string;
  currentContent: string;
}) => {
  const previousBlocks = parseMarkdownBlocks(previousContent);
  const currentBlocks = parseMarkdownBlocks(currentContent);
  const matchedBlocks = matchMarkdownBlocks(previousBlocks, currentBlocks);

  const diffResultMarkdown =
    matchedBlocks.length === 0
      ? renderTextBlockDiff(previousContent, currentContent)
      : matchedBlocks.map(renderMatchedBlockPair).filter(Boolean).join("\n\n");

  return {
    diffResultMarkdown,
    hasDiff: previousContent !== currentContent,
  };
};

const renderMatchedBlockPair = ({
  previousBlock,
  currentBlock,
}: MatchedBlockPair): string => {
  if (currentBlock?.kind === "table" || previousBlock?.kind === "table") {
    return calculateTableDiffMarkdown(
      currentBlock?.rawMarkdown || "",
      previousBlock?.rawMarkdown || ""
    );
  }

  if (
    currentBlock?.kind === "image" ||
    previousBlock?.kind === "image" ||
    currentBlock?.kind === "link" ||
    previousBlock?.kind === "link"
  ) {
    return renderMediaBlock(previousBlock, currentBlock);
  }

  if (previousBlock && currentBlock) {
    if (previousBlock.inlineSegments || currentBlock.inlineSegments) {
      return renderInlineValueDiff(
        previousBlock.rawMarkdown,
        currentBlock.rawMarkdown,
        previousBlock.inlineSegments,
        currentBlock.inlineSegments
      );
    }

    return renderTextBlockDiff(
      previousBlock.rawMarkdown,
      currentBlock.rawMarkdown
    );
  }

  if (currentBlock) {
    if (currentBlock.inlineSegments) {
      return renderInlineValueDiff(
        "",
        currentBlock.rawMarkdown,
        undefined,
        currentBlock.inlineSegments
      );
    }

    return renderTextBlockDiff("", currentBlock.rawMarkdown);
  }

  if (previousBlock) {
    if (previousBlock.inlineSegments) {
      return renderInlineValueDiff(
        previousBlock.rawMarkdown,
        "",
        previousBlock.inlineSegments,
        undefined
      );
    }

    return renderTextBlockDiff(previousBlock.rawMarkdown, "");
  }

  return "";
};

const renderMediaBlock = (
  previousBlock: MatchedBlockPair["previousBlock"],
  currentBlock: MatchedBlockPair["currentBlock"]
): string => {
  const previousMarkdown = previousBlock?.rawMarkdown || "";
  const currentMarkdown = currentBlock?.rawMarkdown || "";

  if (
    previousMarkdown &&
    currentMarkdown &&
    previousMarkdown === currentMarkdown
  ) {
    return currentMarkdown;
  }

  if (previousMarkdown && !currentMarkdown) {
    return createMediaMarkdownWithClass(previousBlock, "removed");
  }

  if (!previousMarkdown && currentMarkdown) {
    return createMediaMarkdownWithClass(currentBlock, "added");
  }

  return [
    createMediaMarkdownWithClass(previousBlock, "removed"),
    createMediaMarkdownWithClass(currentBlock, "added"),
  ]
    .filter(Boolean)
    .join("\n\n");
};

const createMediaMarkdownWithClass = (
  block: MatchedBlockPair["currentBlock"] | MatchedBlockPair["previousBlock"],
  className: "added" | "removed"
): string => {
  if (!block) {
    return "";
  }

  if (block.kind === "image") {
    return `${block.rawMarkdown}{class:"${className}"}`;
  }

  if (block.kind === "link") {
    return createLinkHtml(
      block.imageUrl || "",
      block.mediaLabel || block.imageUrl || block.rawMarkdown,
      className,
      block.mediaTitle
    );
  }

  return `<span class="${className}">${block.rawMarkdown}</span>`;
};

const createLinkHtml = (
  href: string,
  label: string,
  className: "added" | "removed",
  title?: string | null
): string => {
  const titleAttribute = title
    ? ` title="${escapeHtmlAttribute(title)}"`
    : "";

  return `<span class="${className}"><a href="${escapeHtmlAttribute(
    href
  )}"${titleAttribute}>${escapeHtmlText(label)}</a></span>`;
};

const escapeHtmlAttribute = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const escapeHtmlText = (value: string): string =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
